import React, { useContext } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Badge from 'react-bootstrap/Badge'
import Form from 'react-bootstrap/Form'
import useMunicipalitySettings from 'api/hooks/use-municipality-settings'
import { useUpdateAdminMunicipalitySetting } from 'api/hooks/use-admin-municipality-settings'
import UserContext from 'context/user-context'
import { useQueryClient } from 'react-query'

const AI_USE_CASES = [
  {
    title: 'AI Search',
    description:
      'Use natural language to search and filter dashboards. Instead of manually building filters, simply describe what you\'re looking for — for example, "Show transactions over $5,000 from last month."'
  },
  {
    title: 'Account Group Builder',
    description:
      'Describe the account rollups you want in plain language — for example, "group object codes 0100 through 0199 as Salaries & Benefits" — and review the proposed groups before anything is saved. Available on the Account Groups tab in Settings.'
  },
  {
    title: 'Policy AI Extraction',
    description:
      'Upload policy documents as PDFs and let AI automatically extract the rules and criteria defined within them. Extracted rules can be reviewed and applied to your organization\'s workflows.'
  },
  {
    title: 'Role Description Generator',
    description:
      'Generate a description of what a role actually does, based on its current permissions, data access, and segregation-of-duties conflicts. Available on the Details tab of any role.'
  }
]

function AiFeaturesSettings() {
  const { data: settings, isLoading } = useMunicipalitySettings({ options: {} })
  const currentUser = useContext(UserContext)
  const queryClient = useQueryClient()
  const isAdmin = currentUser?.is_admin && currentUser?.email?.endsWith('@thirdline.io')
  const updateMutation = useUpdateAdminMunicipalitySetting({
    municipalityId: currentUser?.municipality_id,
    options: {
      onSuccess: () => queryClient.invalidateQueries('municipality-settings')
    }
  })

  if (isLoading) {
    return <div>Loading...</div>
  }

  const isEnabled = settings?.ai_search_enabled || false

  const handleToggle = () => {
    updateMutation.mutate({ ai_search_enabled: !isEnabled })
  }

  return (
    <Row>
      <Col>
        <Card>
          <Card.Body>
            <Row className="mb-3">
              <Col className="d-flex align-items-center gap-2">
                <div className="text-primary fw-bold">AI Features</div>
                <Badge className="badge-primary" bg="">
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </Col>
              {isAdmin && (
                <Col xs="auto" className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id="ai-features-toggle"
                    checked={isEnabled}
                    onChange={handleToggle}
                    disabled={updateMutation.isLoading}
                  />
                </Col>
              )}
            </Row>

            <Row className="mb-4">
              <Col>
                <div className="p-3 bg-light rounded border">
                  <p className="mb-1 fw-semibold small">Privacy &amp; Data Use</p>
                  <p className="mb-0 text-muted small">
                    No customer data is used to train or improve AI models. All AI processing is performed on your data in isolation and is not shared with or used by any third parties for model training purposes.
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <p className="fw-semibold mb-2 small text-uppercase text-muted">Included capabilities</p>
                {AI_USE_CASES.map(({ title, description }) => (
                  <div key={title} className="mb-3">
                    <p className="mb-1 fw-semibold">{title}</p>
                    <p className="mb-0 text-muted small">{description}</p>
                  </div>
                ))}
              </Col>
            </Row>

            {!isAdmin && (
              <Row>
                <Col>
                  <div className="p-3 border rounded">
                    <p className="mb-1 fw-semibold small">Want to enable or disable AI features?</p>
                    <p className="mb-0 text-muted small">
                      To request a change to your AI features access, please submit a ticket to{' '}
                      <a href="mailto:support@thirdline.io">support@thirdline.io</a>.
                    </p>
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default AiFeaturesSettings
